import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { User, Clock, Calendar, Shield, RefreshCw, Euro } from 'lucide-react';
import Modal from '../ui/Modal';
import { useTranslation } from 'react-i18next';

import StaffDetailsTab from '../staff/StaffDetailsTab';
import StaffHoursTab from '../staff/StaffHoursTab';
import StaffTimeOffTab from '../staff/StaffTimeOffTab';
import StaffShiftChangesTab from '../staff/StaffShiftChangesTab';
import StaffCompensationTab from '../staff/StaffCompensationTab';
import { useUsers } from '../../contexts/UsersContext';
import { useEvents } from '../../contexts/EventsContext';
import { useAuth } from '../../contexts/AuthContext';
import { hasStaffInsights } from '@/lib/planFeatures';
import { StaffMember } from '../../pages/Staff';

interface StaffDetailModalProps {
    staff: StaffMember;
    isOpen: boolean;
    onClose: () => void;
}

const StaffDetailModal: React.FC<StaffDetailModalProps> = ({ staff, isOpen, onClose }) => {
    const { t } = useTranslation();
    const { getUserById, updateUser } = useUsers();
    const { busySlots } = useEvents();
    const { isAdmin, isOwner, subscriptionPlan } = useAuth();
    const [activeTab, setActiveTab] = useState<'details' | 'hours' | 'timeoff' | 'shiftchanges' | 'compensation'>('details');

    // Compenso: dati economici, solo titolare e dal piano AI in su
    const canSeeCompensation = isOwner && hasStaffInsights(subscriptionPlan);

    const fullStaffData = getUserById(staff.id);

    if (!fullStaffData) return null;

    const handleSave = async (data: any) => {
        return await updateUser(staff.id, data);
    };

    const tabs = [
        { id: 'details', label: t('staff.card.details_title'), icon: User }, // "Dettagli Dipendente"
        { id: 'hours', label: t('staff.hours.schedule'), icon: Clock },      // "Orario"
        { id: 'timeoff', label: 'Ferie & Permessi', icon: Calendar },        // Fallback statico o custom label
        { id: 'shiftchanges', label: t('staff.shiftchanges.tab'), icon: RefreshCw },
        // Compenso: solo per il titolare (dati economici), dal piano AI in su
        ...(canSeeCompensation ? [{ id: 'compensation', label: 'Compenso', icon: Euro }] : []),
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-4xl h-full md:h-auto md:max-h-[90vh] flex flex-col"
            headerClassName="bg-white border-b border-gray-100 pb-0 flex-shrink-0"
            headerContent={
                <div className="pb-2">
                    {/* Header con Info Staff */}
                    <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5 mb-6">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold uppercase ${staff.avatarColor} shadow-sm shrink-0`}>
                            {staff.firstName[0]}{staff.lastName[0]}
                        </div>

                        <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{fullStaffData.first_name} {fullStaffData.last_name}</h2>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className="text-sm text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded-lg flex items-center gap-1">
                                    <Shield size={12} /> {(fullStaffData.role || staff.role) === 'admin' ? 'Admin' : 'Staff'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex overflow-x-auto custom-scrollbar -mx-6 px-6 md:mx-0 md:px-0 border-b md:border-none border-gray-100 scroll-smooth">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`relative flex-1 md:flex-none px-4 md:px-6 py-3 rounded-t-xl text-sm font-bold flex items-center justify-center md:justify-start gap-2 transition-all whitespace-nowrap min-w-fit ${activeTab === tab.id
                                    ? 'text-clip-600 bg-gray-50 md:bg-gray-50'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50/50'
                                    }`}
                            >
                                <tab.icon size={16} className={activeTab === tab.id ? 'text-clip-500' : 'text-gray-400'} />
                                {tab.label}
                                {activeTab === tab.id && (
                                    <div
                                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-clip-500 z-10"
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            }
        >
            <div className="h-full overflow-y-auto custom-scrollbar bg-white">
                <AnimatePresence mode='wait'>
                    {activeTab === 'details' && (
                        <StaffDetailsTab
                            data={fullStaffData}
                            onSave={handleSave}
                            onUpdate={() => { }}
                            readOnly={!isAdmin}
                        />
                    )}
                    {activeTab === 'hours' && (
                        <StaffHoursTab
                            data={fullStaffData}
                            onSave={handleSave}
                            readOnly={!isAdmin}
                        />
                    )}
                    {activeTab === 'timeoff' && (
                        <StaffTimeOffTab
                            userId={staff.id}
                            busySlots={busySlots || []}
                            readOnly={!isAdmin}
                        />
                    )}
                    {activeTab === 'shiftchanges' && (
                        <StaffShiftChangesTab
                            userId={staff.id}
                            readOnly={!isAdmin}
                        />
                    )}
                    {activeTab === 'compensation' && canSeeCompensation && (
                        <StaffCompensationTab
                            data={fullStaffData}
                            readOnly={!isOwner}
                        />
                    )}
                </AnimatePresence>
            </div>
        </Modal>
    );
};

export default StaffDetailModal;